import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { get } from "lodash";
import { withElementWrapper } from "../withElementWrapper";
import { useDeviceType } from "../../../../../components/customHooks/useDeviceType";
import { getCategoryAndSubCategory, getTemplateName } from "../../../../../ga-utils";
import { getAdSlots } from "../../../../../components/ads/utils";
import "./text.m.css";

const RANK_PRIORITY = {
  fortune500ranks: 4,
  next500ranks: 3,
  mpwranks: 2,
  "40u40ranks": 1,
  emergingcompaniesranks: 5,
};

const RANK_LABELS = {
  fortune500ranks: "Fortune 500 India",
  next500ranks: "The Next 500",
  mpwranks: "Most Powerful Women",
  "40u40ranks": "Forty Under Forty",
  emergingcompaniesranks: "The Emerging Companies",
};

const TextBase = ({ element = {}, AdComponent, story, ...restProps }) => {
  const content = element?.text?.replace(/&nbsp;/g, " ");
  if (!content) return null;
  const isMobile = useDeviceType() !== "desktop";

  const { "linked-entities": linkedEntities = [] } = story || {};
  const newTextElement = content.match(/(<([^>\s]+)(?:\s[^>]*)?>.*?<\/\2>|<[^>\s]+(?:\s[^>]*)?\/>)/g);

  const entities = linkedEntities.filter((entity) => ["company", "individual"].includes(entity?.type));
  if (!newTextElement) return null;

  // Function to render DFP ad in popup
  const renderPopupAd = (popupElement, qtState, popupId, retryCount = 0) => {
    // Check if popup still exists in DOM
    if (!popupElement || !document.contains(popupElement)) {
      console.log("Popup no longer exists, skipping ad render for:", popupId);
      return;
    }

    // Check if ad is already loaded for this popup
    const checkBaseId = isMobile ? "div-gpt-ad-1746600300285-0" : "div-gpt-ad-1746600074592-0";
    const existingAd = popupElement.querySelector(`#${checkBaseId}-${popupId}`);
    if (existingAd && existingAd.children.length > 0) {
      console.log("Ad already loaded for popup:", popupId);
      return;
    }

    const networkId = get(qtState, ["config", "ads-config", "network_id"], "");
    const adsConfig = get(qtState, ["config", "ads-config", "dfp_ads"], {});
    const enableAds = get(adsConfig, ["enable_ads"], false);
    console.log("enableAds", enableAds);
    console.log("networkId", networkId);
    if (!enableAds || !networkId) return;

    // Check if DFP is ready
    if (!window.googletag || !window.googletag.apiReady) {
      console.log("DFP not ready, retrying in 100ms");
      if (retryCount < 10) { // Max 10 retries
        setTimeout(() => renderPopupAd(popupElement, qtState, popupId, retryCount + 1), 100);
      }
      return;
    }

    // Create ad container with unique div IDs for logo widget ads
    const adContainer = document.createElement("div");
    const baseId = isMobile ? "div-gpt-ad-1746600300285-0" : "div-gpt-ad-1746600074592-0";
    adContainer.id = `${baseId}-${popupId}`;
    adContainer.style.cssText = `
      width: ${isMobile ? "74px" : "74px"};
      height: ${isMobile ? "14px" : "16px"};
      margin-top: 8px;
      border-radius: 4px;
      overflow: visible;
      background: #f5f5f5;
      display: block;
      position: relative;
      z-index: 1;
    `;

    // Find the ad placeholder and replace it with the ad container
    const adPlaceholder = popupElement.querySelector(`#ad-placeholder-small-${popupId}`);
    if (!adPlaceholder) {
      console.log("Ad placeholder not found for popup:", popupId);
      // Retry after a short delay in case DOM is still rendering
      if (retryCount < 5) {
        setTimeout(() => renderPopupAd(popupElement, qtState, popupId, retryCount + 1), 50);
      }
      return;
    }

    // Clear and append the ad container
    adPlaceholder.innerHTML = "";
    adPlaceholder.appendChild(adContainer);

    // Configure ad slot for popup - using logo widget ads from ads.yml
    const pageType = get(qtState, ["pageType"], "");
    const pageConfig = get(qtState, ["config", "ads-config", "ad_slots", pageType], {});
    const desktopAdUnit = get(pageConfig, ["desktop", "sponsor", "ad_unit"], "");
    const mobileAdUnit = get(pageConfig, ["mobile", "sponsor", "ad_unit"], "");

    const adUnit = isMobile ? mobileAdUnit : desktopAdUnit;

    // Check if ad unit is available
    if (!adUnit) {
      console.log("Ad unit not found for page type:", pageType, "device:", isMobile ? "mobile" : "desktop");
      return;
    }

    const adPath = `/${networkId}/${adUnit}`;
    const adSize = isMobile ? [[74, 14]] : [[74, 16]]; // Using logo widget ad sizes
    const adSlotConfigName = isMobile ? "74x14_ad" : "74x16_ad";

    console.log("Rendering ad for popup:", popupId, "with container ID:", adContainer.id, "ad unit:", adUnit);

    // Wait for popup to be fully visible before loading ad
    setTimeout(() => {
      // Check if popup is still visible and ad container is properly positioned
      if (popupElement && document.contains(popupElement) && popupElement.style.visibility === "visible") {
        // Ensure ad container is visible and properly sized
        const containerRect = adContainer.getBoundingClientRect();
        if (containerRect.width > 0 && containerRect.height > 0) {
          console.log("Ad container is visible and properly sized:", containerRect);

          // Render DFP ad with synchronous loading for better reliability
          getAdSlots({
            path: adPath,
            size: adSize,
            id: adContainer.id,
            qtState,
            viewPortSizeMapping: [
              { viewport: [0, 0], sizes: adSize }
            ],
            storySectionSlug: get(qtState, ["data", "story", "sections", 0, "slug"], ""),
            loadAdsSynchronously: true, // Changed to synchronous for better reliability
            delayPeriod: 0, // No delay for immediate loading
            refreshAdUnit: false,
            outOfPage: false,
            lazyLoadRefreshAdUnit: true,
          });
        } else {
          console.log("Ad container not properly sized, retrying...");
          if (retryCount < 3) {
            setTimeout(() => renderPopupAd(popupElement, qtState, popupId, retryCount + 1), 200);
          }
        }
      }
    }, 100); // Small delay to ensure popup is fully rendered

    // Add a listener to detect when the ad is loaded
    setTimeout(() => {
      const adElement = document.getElementById(adContainer.id);
      if (adElement && adElement.children.length > 0) {
        console.log("Ad successfully loaded for popup:", popupId, "with ID:", adContainer.id);

        // Force refresh the ad slot to ensure creative displays
        if (window.googletag && window.googletag.pubads) {
          try {
            const slot = window.googletag.pubads().getSlots().find(s => s.getSlotElementId() === adContainer.id);
            if (slot) {
              window.googletag.pubads().refresh([slot]);
              console.log("Forced refresh for ad slot:", adContainer.id);
            }
          } catch (error) {
            console.warn('Error refreshing ad slot:', error);
          }
        }
      } else {
        console.log("Ad not loaded for popup:", popupId, "with ID:", adContainer.id, "retrying...");
        if (retryCount < 2) { // Reduced retry attempts
          setTimeout(() => renderPopupAd(popupElement, qtState, popupId, retryCount + 1), 500); // Increased retry delay
        }
      }
    }, 1000); // Increased check delay to allow ad to load
  };

  const getLatestRank = (entity) => {
    let selected = null;

    ["40u40ranks", "mpwranks", "fortune500ranks", "next500ranks", "emergingcompaniesranks"].forEach((key) => {
      if (!entity[key]) return;

      let rankArray = [];
      try {
        rankArray = JSON.parse(entity[key]);
        if (!Array.isArray(rankArray)) return;
      } catch (error) {
        console.error(`Error parsing ${key} for entity ${entity.name || entity.slug}:`, error);
        return;
      }

      rankArray.forEach((rankObj) => {
        const year = Object.keys(rankObj)[0];
        const rank = rankObj[year];

        if (
          !selected ||
          Number(year) > selected.year ||
          (Number(year) === selected.year && RANK_PRIORITY[key] > RANK_PRIORITY[selected.type])
        ) {
          selected = {
            year: Number(year),
            rank,
            type: key,
          };
        }
      });
    });

    if (selected) {
      return {
        year: selected.year,
        rank: selected.rank,
        label: RANK_LABELS[selected.type],
      };
    }

    return null;
  };

  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const inTextHoverPopup = get(qtState, ["config", "publisher-attributes", "inTextHoverPopup"], false);

  // Function to track entity hyperlink clicks
  const trackEntityHyperlinkClick = (entityName, href = "") => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const pageType = qtState.pageType;
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        qtState.config,
        story
      );
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "entity_hyperlink_click",
        link_text: entityName,
        link_url: href,
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(story?.["story-template"] || pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  // Make tracking function available to window for the onclick attribute
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.trackEntityHyperlinkClick = trackEntityHyperlinkClick;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete window.trackEntityHyperlinkClick;
      }
    };
  }, []);

  useEffect(() => {
    let popup;
    let currentAnchor = null;
    let popupCounter = 0;

        const removePopup = () => {
      if (popup) {
        // Clean up any existing ads in the popup
        const adContainers = popup.querySelectorAll('[id^="div-gpt-ad-1746600074592-0"], [id^="div-gpt-ad-1746600300285-0"]');
        adContainers.forEach(adContainer => {
          if (adContainer.id && window.googletag && window.googletag.destroySlots) {
            try {
              const slot = window.googletag.pubads().getSlots().find(s => s.getSlotElementId() === adContainer.id);
              if (slot) {
                window.googletag.destroySlots([slot]);
                console.log("Cleaned up ad slot:", adContainer.id);
              }
            } catch (error) {
              console.warn('Error cleaning up ad slot:', error);
            }
          }
        });

        popup.remove();
        popup = null;
        currentAnchor = null;
      }
    };

    const handleInteraction = (e) => {
      const slug = e.target.getAttribute("data-hover-slug");
      if (!slug) return;
      e.preventDefault();
      const entity = linkedEntities.find((ent) => ent.slug === slug);
      if (!entity) return;

      const { photo, "display-name": displayName, designation, name, company } = entity;
      const latestRank = getLatestRank(entity);

      // Generate unique popup ID
      popupCounter++;
      const popupId = `popup-${Date.now()}-${popupCounter}-${Math.random().toString(36).substr(2, 9)}`;

      popup = document.getElementById("hover-popup");
      if (!popup) {
        removePopup();
        currentAnchor = e.target;

        popup = document.createElement("div");
        popup.id = "hover-popup";

        popup.innerHTML = `
        <div>
        <div style="
          position: relative;
          display: flex;
          width: auto;
          max-width: ${isMobile ? `331px` : `431px`};
          height: auto;
          gap: 12px;
          padding: ${isMobile ? `12px` : `16px`};
          border-radius: 8px;
          background: white;
          z-index: 98;
          border: 1px solid rgba(0,0,0, 0.25);
        ">
          ${
            isMobile
              ? `<button id="popup-close-btn" style="
                  position: absolute;
                  top: -1px;
                  right: 8px;
                  background: none;
                  border: none;
                  color: black;
                  font-size: 24px;
                  cursor: pointer;
                ">×</button>`
              : ""
          }
          <a href=/${displayName ? `people` : `companies`}/${slug}
            onclick="event.preventDefault(); if (typeof window !== 'undefined') {
              window.trackEntityHyperlinkClick('${displayName || name}' , href); window.location.href='/${
          displayName ? "people" : "companies"
        }/${slug}'; }"
            style="display: flex; gap: 12px; text-decoration: none; color: inherit; width: 100%;">
            <span style="
              flex-shrink: 0;
              background-image: url('https://media.fortuneindia.com/fortuneindia-uat/2025-07-21/cyhsz12n/fallback-image.svg?auto=format,compress');
              background-color: transparent;
              background-position: center;
              background-repeat: no-repeat;
              background-size: cover;
              overflow: hidden;
              width: ${isMobile ? `82px` : `110px`};
              height: ${isMobile ? `82px` : `110px`};
              border-radius: 8px
              ">
              ${
                photo?.[0]?.url
                  ? `<img src="${photo[0].url}?w=220&quality=70" style="width: ${
                      isMobile ? `82px` : `110px`
                    }; height: ${
                      isMobile ? `82px` : `110px`
                    }; object-fit: cover; border-radius: 8px; border: 2px solid rgba(0, 0, 0, 0.08);" />`
                  : displayName
                  ? `<div style="width: ${isMobile ? "82px" : "110px"}; height: ${
                      isMobile ? "82px" : "110px"
                    }; background: #F5F5F5; border-radius: 8px;">
                      <svg viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg" style="width: 100%; height: 100%; border-radius: 8px; border: 2px solid rgba(0, 0, 0, 0.08);">
                        <rect width="52" height="52" fill="#F5F5F5" />
                        <circle cx="26" cy="15.8889" r="5.8889" fill="#D8D8D8" />
                        <path
                          d="M11.5556 40.4444C11.5556 32.5 18.0556 26 26 26C33.9444 26 40.4444 32.5 40.4444 40.4444H11.5556Z"
                          fill="#D8D8D8"
                        />
                      </svg>
                    </div>
                    `
                  : `<div style="width: ${isMobile ? "82px" : "110px"}; height: ${
                      isMobile ? "82px" : "110px"
                    }; background: #F5F5F5; border-radius: 8px;">
                    <svg style="border: 2px solid rgba(0, 0, 0, 0.08); border-radius: 8px;" width=${
                      isMobile ? "82" : "110"
                    } height=${
                      isMobile ? "82" : "110"
                    } viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" >
                    <rect width="80" height="80" rx="12" fill="#F5F5F5"/>
                    <path d="M20.2413 41.235V72.087H8V48.4437L20.2413 41.235Z" fill="#D8D8D8"/>
                    <path d="M53.2607 24V72.0869H48.5094V56.8888C48.5094 55.0784 47.0574 53.6091 45.2684 53.6091H34.7157C32.9234 53.6091 31.4747 55.0784 31.4747 56.8888V72.0869H26.7234V24H53.2607Z" fill="#D8D8D8"/>
                    <path d="M71.9999 72.087H59.7424V48.939L71.9837 56.1445L71.9999 72.087Z" fill="#D8D8D8"/>
                    <path d="M42.0272 60.1685H37.9565V72.0869H42.0272V60.1685Z" fill="#D8D8D8"/>
                    </svg>
                  </div>
                      `
              }
            </span>
            <div style="display: flex; flex-direction: column; gap: 4px; flex: 1; justify-content: ${
              inTextHoverPopup ? `flex-start` : `center`
            }">
              <span style="
                overflow: hidden;
                color: var(--Black, #121212);
                text-overflow: ellipsis;
                font-family: Frank Ruhl Libre;
                font-size: ${isMobile ? `var(--font-xl)` : `var(--font-xxl)`};
                font-style: normal;
                font-weight: 700;
                word-break: break-word;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 3;
                padding-right: ${isMobile ? `14px` : `0px`};
                line-height: 104%;">${displayName || name}</span>
              ${
                displayName
                  ? `<span style="
                overflow: hidden;
                color: var(--Black, #121212);
                text-overflow: ellipsis;
                font-family: Roboto Slab;
                font-size: var(--font-m);
                font-style: normal;
                font-weight: 400;
                word-break: break-word;
                opacity: 0.9;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 1;
                line-height: normal;">${designation}${displayName && company ? `, ${company}` : ``}</span>`
                  : ""
              }
              ${
                latestRank
                  ? `<span style="
                opacity: 0.9;
                color: var(--Brand, #C6554F);
                font-family: Roboto Slab;
                word-break: break-word;
                font-size: ${isMobile ? `var(--font-s)` : `var(--font-l)`};
                font-style: normal;
                font-weight: 700;
                line-height: ${isMobile ? `normal` : `160%`};">
                ${latestRank.label === "Forty Under Forty" ? "" : `#${latestRank.rank}`} ${latestRank.label} ${
                      latestRank.year
                    }
              </span> `
                  : ""
              }
              ${
                inTextHoverPopup
                  ? `
                  <div
                    style="color: var(--Light-Grey, #7C8288);
                    text-align: right;
                    font-family: Roboto Slab;
                    font-size: 8px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 160%;
                    letter-spacing: 0.32px;
                    align-self: flex-end;
                    margin-top: auto"
                  >
                    <span>Powered by</span>
                    <span id="ad-placeholder-small-${popupId}"></span>
                  </div>
                `
                  : ""
              }
            </div>
          </a>
        </div>
          ${
            isMobile
              ? ""
              : `<div style="
          width: 18px;
          height: 18px;
          background-color: white;
          transform: rotate(45deg);
          margin-top: -10px;
          border: 1px solid rgba(0,0,0, 0.25);
          border-left: 0px;
          border-top: 0px;
          z-index: 99;
          position: relative;
          margin-left: 48%;
          }px"></div>`
          }
          </div>
      `;

        popup.style.position = "absolute";
        popup.style.visibility = "hidden";
        document.body.appendChild(popup);
        requestAnimationFrame(() => {
          // Ensure window is defined before using its properties
          if (typeof window === "undefined") return;

          const popupHeight = popup.getBoundingClientRect().height;
          const targetTop = e.target.getBoundingClientRect().top + window.scrollY;
          popup.style.top = popupHeight > 170 ? `${targetTop - popupHeight - 25}px` : `${targetTop - popupHeight}px`;
          if (isMobile) {
            const rawLeft = e.clientX - popup.getBoundingClientRect().width / 2;
            const minLeft = 16;
            const maxLeft = window.innerWidth - popup.getBoundingClientRect().width - 16;
            const clampedLeft = Math.max(minLeft, Math.min(rawLeft, maxLeft));
            popup.style.left = `${clampedLeft}px`;
          } else {
            popup.style.left = `${e.clientX - popup.getBoundingClientRect().width / 2}px`;
          }
          popup.style.visibility = "visible";

          // Render DFP ad in popup after it's visible and DOM is ready
          if (inTextHoverPopup) {
            // Use a small delay to ensure DOM is fully rendered
            setTimeout(() => {
              // Check if this is still the current popup before rendering ad
              if (popup && document.contains(popup)) {
                renderPopupAd(popup, qtState, popupId);
              }
            }, 50);
          }
        });

        if (isMobile) {
          const closeBtn = document.getElementById("popup-close-btn");
          if (closeBtn) {
            closeBtn.addEventListener("click", (e) => {
              e.stopPropagation();
              removePopup();
            });
          }
        }
      }
    };

    const handleMouseEnter = (e) => {
      if (!isMobile) handleInteraction(e);
    };

    const handleClickMobile = (e) => {
      if (isMobile) handleInteraction(e);
    };

    const handleDocumentMouseMove = (e) => {
      if (isMobile || !popup || !currentAnchor) return;

      const isOverPopup = popup.contains(e.target);
      const isOverAnchor = currentAnchor.contains(e.target);
      if (!isOverPopup && !isOverAnchor) {
        removePopup();
      }
    };

    const handleTouchOutside = (e) => {
      if (!isMobile || !popup || !currentAnchor) return;

      const isOverPopup = popup.contains(e.target);
      const isOverAnchor = currentAnchor.contains(e.target);
      if (!isOverPopup && !isOverAnchor) {
        removePopup();
      }
    };

    const links = document.querySelectorAll("a[data-hover-slug]");
    links.forEach((link) => {
      if (isMobile) {
        link.addEventListener("click", handleClickMobile);
      } else {
        link.addEventListener("mouseenter", handleMouseEnter);
      }
    });

    document.addEventListener("mousemove", handleDocumentMouseMove);
    document.addEventListener("touchstart", handleTouchOutside);

    return () => {
      links.forEach((link) => {
        if (isMobile) {
          link.removeEventListener("click", handleClickMobile);
        } else {
          link.removeEventListener("mouseenter", handleMouseEnter);
        }
      });
      document.removeEventListener("mousemove", handleDocumentMouseMove);
      document.removeEventListener("touchstart", handleTouchOutside);
      removePopup();
    };
  }, [content, linkedEntities, isMobile]);

  return newTextElement.map((item, index) => {
    if (item.includes("ad_goes_here")) {
      const adIndexNo = JSON.parse(item.replace(/<[^>]*>/g, "")).index;
      return AdComponent ? <AdComponent key={index} adIndexNo={adIndexNo} /> : null;
    }

    const linkRegex = /<a\s+([^>]*href="([^"]+)"[^>]*)entity-slug="([^"]+)"([^>]*)>([^<]*)<\/a>/gi;
    const anchorTags = item.match(linkRegex);
    if (!anchorTags)
      return (
        <div
          className="arrow-component arr--text-element"
          styleName="text-element"
          data-test-id="text"
          id="text-element"
          key={index}
          dangerouslySetInnerHTML={{ __html: item }}
          {...restProps}
        />
      );

    let updatedHTML = item;
    anchorTags.forEach((tag) => {
      const updatedTag = tag.replace(
        /<a\s+([^>]*href="([^"]+)"[^>]*)entity-slug="([^"]+)"([^>]*)>([^<]*)<\/a>/i,
        (match, beforeHrefAttrs, href, slug, afterSlugAttrs, linkText) => {
          const entity = entities.find((e) => e.slug === slug);
          if (!entity) return match;

          return `<a ${beforeHrefAttrs}entity-slug="${slug}" ${afterSlugAttrs}
                    data-hover-slug="${slug}"
                    data-entity-name="${entity.name || ""}"
                    href="#"
                    >
                    ${linkText}
                  </a>`;
        }
      );
      updatedHTML = updatedHTML.replace(tag, updatedTag);
    });

    return (
      <div
        className="arrow-component arr--text-element"
        styleName="text-element"
        data-test-id="text"
        id="text-element"
        key={index}
        dangerouslySetInnerHTML={{ __html: updatedHTML }}
        {...restProps}
      />
    );
  });
};

TextBase.propTypes = {
  element: PropTypes.shape({ text: PropTypes.string }),
  AdComponent: PropTypes.node,
  story: PropTypes.object,
};

export const Text = withElementWrapper(TextBase);
